import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, type Ref, unref } from "vue";
import type { DynamicRecord } from "@/shared/contracts/structure";
import { formatNumber, panelContext } from "@/shared/format";
import { HttpError, request } from "@/shared/http/client";

export interface Stage {
	readonly id: string;
	readonly code: string;
	readonly name: string;
	readonly color: string;
	readonly position: number;
	readonly isTerminal: boolean;
	/** What the stage writes onto the record, so a board and a column agree. */

	/**
	 * The goal of this step, and the readable criterion for leaving it.
	 *
	 * CONTEXT for an agent, never a prompt (ADR-AG2). One sentence each — a
	 * paragraph here is someone writing behaviour into a stage.
	 */
	readonly objective: string | null;
	readonly exitWhen: string | null;
}

export interface BoardCard {
	/**
	 * Null for a record a mirroring board shows with no card of its own yet — an order that
	 * exists but nobody has dragged. It sits in the first stage; dragging it PLACES it (makes
	 * the card) instead of moving one. A curated funnel never sends null.
	 */
	readonly id: string | null;
	readonly stageId: string;
	readonly recordId: string;
	readonly position: number;
	readonly enteredStageAt: string;
	readonly record: DynamicRecord | null;
}

/**
 * A whole column, counted on the server.
 *
 * The page the browser holds is not the column: a stage with 213 cards answers with 50,
 * and a count taken from `cards.length` would say 50. The sum has the same problem and a
 * worse consequence — "R$ 42.300" over a quarter of a column is a number somebody
 * forecasts with. Both are computed where every row is.
 */
export interface StageTotal {
	/** Every card in the stage, not just the ones loaded. */
	readonly count: number;
	/** The value field summed over the whole stage, or null when the funnel has none. */
	readonly sum: number | null;
}

export interface BoardView {
	readonly code: string;
	readonly name: string;
	/** What the funnel is for. Editable on its settings; never used in logic. */
	readonly description: string | null;
	/**
	 * Field codes shown on a card, in order. The first is the title.
	 * Empty means the board falls back to the first live fields.
	 */
	readonly cardFields: readonly string[];
	/**
	 * The same choice, already resolved against the entity — code, label and type.
	 *
	 * It exists because the server now PROJECTS the record down to these fields: guessing
	 * the fallback in the browser ("the first three that show in lists") would read codes
	 * the projection did not send, and every card would go blank. Optional only so an
	 * older server still draws a board; absent means the old guess applies.
	 */
	readonly resolvedCardFields?: ReadonlyArray<{
		readonly code: string;
		readonly label: string;
		readonly type: string;
	}>;
	readonly entityCode: string;
	/**
	 * O nome da tabela de dados do funil e se ela está ARQUIVADA (QA 19/10): o funil
	 * fica na lista, mas parado — sem cartão novo — e a tela diz por quê, com o nome.
	 */
	readonly entityName?: string | null;
	readonly entityArchived?: boolean;
	readonly stages: readonly Stage[];
	readonly cards: readonly BoardCard[];
	/** By stage CODE. Absent means the response is the whole funnel, as it always was. */
	readonly stageTotals?: Readonly<Record<string, StageTotal>>;
}

/**
 * UM DADO QUE O FUNIL EXIGE DE TODO CARD QUE NASCE NELE.
 *
 * A MESMA forma em dois lugares — na lista de funis (`birthFields`) e na recusa
 * `MISSING_REQUIRED` que a colocação devolve. Um leitor, um formato: quem desenha o
 * campo não precisa saber por qual dos dois caminhos ele chegou.
 *
 * `entity` é a entidade DONA, que nem sempre é a do funil: com o salto
 * `ref_entity` → `ref_contact` a exigência pode ser da ficha-pessoa. `type` e
 * `options` viajam junto porque quem recebe tem de DESENHAR o campo, e um `select`
 * sem as suas opções é uma caixa de texto a fingir que é uma escolha.
 */
export interface BirthGap {
	readonly entity: string;
	readonly code: string;
	readonly label: string;
	readonly type: string;
	/** Só quando o campo tem `config.options` — `select` e `multi_select`. */
	readonly options?: readonly string[];
}

/**
 * As lacunas lidas do ERRO, e não da frase.
 *
 * A lista de funis pode estar velha — alguém tornou um campo obrigatório enquanto o
 * popover estava aberto — e então a recusa é a única fonte que sabe o que falta. Lida
 * por `payload` e não por `instanceof HttpError` pelo mesmo motivo escrito em
 * `shared/field-gaps/use-field-gaps.ts`: os specs trocam o módulo do cliente por um
 * dublê, e um `instanceof` contra a classe verdadeira devolveria `null` em todos eles.
 *
 * `null` quer dizer "esta falha não é esta" — quem chama mostra a frase do servidor,
 * que é o certo para um 409 ou um 403.
 */
export function refusedBirthFields(error: unknown): readonly BirthGap[] | null {
	const payload = (error as { payload?: { error?: string; fields?: unknown } } | null)?.payload;
	if (payload?.error !== "MISSING_REQUIRED" || !Array.isArray(payload.fields)) return null;
	const gaps = payload.fields
		.map((f): BirthGap | null => {
			// Um servidor mais velho manda `string[]`. O código sozinho ainda desenha um
			// campo de texto com o código por rótulo — feio, e muito melhor do que um
			// beco sem saída.
			if (typeof f === "string") return { entity: "", code: f, label: f, type: "text" };
			const gap = f as Partial<BirthGap>;
			if (typeof gap?.code !== "string") return null;
			return {
				entity: typeof gap.entity === "string" ? gap.entity : "",
				code: gap.code,
				label: typeof gap.label === "string" && gap.label ? gap.label : gap.code,
				type: typeof gap.type === "string" && gap.type ? gap.type : "text",
				...(Array.isArray(gap.options) ? { options: [...gap.options] } : {}),
			};
		})
		.filter((gap): gap is BirthGap => gap !== null);
	// Uma recusa sem campo nenhum não tem o que desenhar.
	return gaps.length > 0 ? gaps : null;
}

export interface PipelineSummary {
	/**
	 * The uuid, alongside the code.
	 *
	 * Membership is by SUBJECT ID — `kind=pipeline, subjectId=<uuid>` — so the panel that
	 * says who may see a funnel needs this one. A screen holding only the code would save
	 * against a key nothing ever reads back, and fail without saying so.
	 */
	readonly id: string;
	readonly code: string;
	readonly name: string;
	/**
	 * Para que serve este funil, nas palavras de quem o criou.
	 *
	 * A rota manda desde sempre e o tipo não a declarava — então a lista de funis e a
	 * visão geral não tinham como procurar nem mostrar o que já vinha no fio. Opcional
	 * como as outras: um servidor mais velho não a manda, e `null` é "não escreveram".
	 */
	readonly description?: string | null;
	readonly entityCode: string;
	/**
	 * O nome da tabela de dados do funil e se ela está ARQUIVADA (QA 19/10): o funil
	 * fica na lista, mas parado — sem cartão novo — e a tela diz por quê, com o nome.
	 */
	readonly entityName?: string | null;
	readonly entityArchived?: boolean;

	/**
	 * Whether this funnel can hold a PERSON — i.e. whether its entity has a
	 * `ref_contact` field. Computed by the backend from the entity's fields, so a
	 * tenant who named the field `cliente` still gets a usable funnel.
	 */
	readonly holdsContacts: boolean;
	/**
	 * O que este funil EXIGE de todo card que nasce nele — vazio quer dizer que um
	 * clique basta.
	 *
	 * É o que deixa o menu OFERECER o funil exigente em vez de o esconder: a nota diz
	 * que ele pede mais, e o passo seguinte desenha os campos ali mesmo. Sem isto, ou
	 * o funil sumia do menu sem explicação ou o clique morria numa recusa.
	 *
	 * Opcional pelo mesmo motivo dos outros deste tipo: um servidor mais velho não a
	 * manda, e ausente é "não sei" — que aqui se lê como "nenhuma", porque a recusa da
	 * colocação continua a dizer a verdade a tempo.
	 */
	readonly birthFields?: readonly BirthGap[];
	readonly stages: readonly Stage[] /** The funnel that opens first. */;
	readonly isDefault?: boolean;
	/**
	 * Quantos cards este funil tem, e quantas pessoas foram marcadas no acesso.
	 *
	 * O que separa um funil do outro numa lista de cinco: qual tem trabalho dentro e
	 * qual só algumas pessoas veem. Vêm da MESMA consulta que já lista os funis, para
	 * comparar cinco não custar cinco quadros carregados.
	 *
	 * Opcionais pelo mesmo motivo escrito em `resolvedCardFields`: um bundle novo contra
	 * uma API velha ainda tem de desenhar a tela. Ausente significa "não sei", e quem lê
	 * escreve a frase sem eles em vez de escrever zero.
	 */
	readonly cardCount?: number;
	/** Zero — ou ausente — significa que TODO MUNDO vê: `membership` lê ao contrário. */
	readonly memberCount?: number;
	/**
	 * Os mesmos cards separados por etapa TERMINAL — "12 cards · 3 em aberto".
	 *
	 * É o número que diz se um funil está trabalhando ou só cheio, e a razão de ele
	 * vir do servidor: `cards.length` conta a página, não a coluna.
	 *
	 * `openCount + closedCount` pode dar MENOS que `cardCount` — um card cuja etapa
	 * foi apagada não está de nenhum dos dois lados. Opcionais pelo mesmo motivo dos
	 * de cima; ausente é "não sei", e quem lê não escreve a frase.
	 */
	readonly openCount?: number;
	readonly closedCount?: number;
	/**
	 * Quando a FORMA do funil mudou pela última vez, em ISO — renomear, arquivar,
	 * virar padrão, ganhar etapa.
	 *
	 * `null` é uma resposta: um servidor que não lê a coluna não sabe, e a lista
	 * deixa a frase "atualizado em" de fora em vez de datar o funil de hoje.
	 */
	readonly updatedAt?: string | null;
}

export interface BoardParent {
	parentEntity: string;
	parentId: string;
	relation: string;
}

export const boardKeys = {
	pipelines: () => ["board", "pipelines"] as const,
	archived: () => ["board", "pipelines", "archived"] as const,
	/** Everything about one funnel — the prefix invalidation is written against. */
	board: (code: string) => ["board", code] as const,
	/**
	 * ONE reading of the board. The search term is part of the key because it is part of
	 * the answer: the same funnel searched for "acme" is a different set of cards, and a
	 * shared key would hand the filtered board back as the whole one. Nested under
	 * `board(code)` so a single invalidate still refreshes every reading.
	 */
	view: (code: string, search: string, parent?: BoardParent) =>
		parent
			? (["board", code, "view", search, parent] as const)
			: (["board", code, "view", search] as const),
};

export function usePipelines(enabled: Ref<boolean> | boolean = true) {
	return useQuery({
		enabled: computed(() => unref(enabled)),
		queryKey: boardKeys.pipelines(),
		queryFn: async (): Promise<PipelineSummary[]> => {
			const res = await request<{ pipelines: PipelineSummary[] }>("/v3/board/pipelines");
			return res.pipelines;
		},
		staleTime: 60_000,
	});
}

/**
 * The funnels somebody put away, fetched only when they go looking.
 *
 * Archiving had no way back on any screen: the funnel left every list and the only
 * caller of `{ archived: false }` was code nobody could reach. `enabled` because this
 * is behind a click — a board opening should not pay for a list nobody asked for.
 */
export function useArchivedPipelines(enabled: Ref<boolean>) {
	return useQuery({
		queryKey: boardKeys.archived(),
		enabled,
		queryFn: async (): Promise<PipelineSummary[]> => {
			const res = await request<{ pipelines: PipelineSummary[] }>(
				"/v3/board/pipelines?archived=true",
			);
			return res.pipelines;
		},
		staleTime: 60_000,
	});
}

/** Brings one back. The same PATCH that puts it away, and the only way out of `archived_at`. */
export function useRestorePipeline() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (code: string) =>
			request(`/v3/board/pipelines/${code}`, { method: "PATCH", body: { archived: false } }),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["board"] });
			void client.invalidateQueries({ queryKey: ["structure"] });
			void client.invalidateQueries({ queryKey: ["records"] });
		},
	});
}

/**
 * QUANTOS CARDS DE CADA COLUNA VÊM DE UMA VEZ.
 *
 * Exportado porque agora há DUAS telas a ler o mesmo funil: o quadro e o hub de
 * configuração. As duas partilham a chave `boardKeys.view(code, "")` — a chave
 * não carrega o `perStage` —, então uma delas a pedir uma página menor deixaria
 * a outra a desenhar a resposta curta a partir do cache, com uma coluna de 213
 * cards a mostrar um. Uma constante, um tamanho, uma entrada de cache.
 */
export const BOARD_PER_STAGE = 50;

/**
 * The board, a page of each column at a time.
 *
 * It used to ask for `/pipelines/:code` bare and receive every card of every stage with
 * the whole entity row inside each — a funnel with a thousand deals meant a thousand
 * complete rows on the wire before the first column could be drawn. `perStage` caps what
 * comes back and `search` filters WHERE THE ROWS ARE, so a term narrows the funnel
 * instead of narrowing what was already downloaded.
 *
 * Both are omitted when nothing asks for them, and a server that ignores them answers
 * exactly what it always answered.
 */
export function useBoard(
	code: Ref<string> | string,
	options?: { search?: Ref<string>; perStage?: number; parent?: Ref<BoardParent | undefined> },
) {
	const pipelineCode = computed(() => unref(code));
	const term = computed(() => (options?.search ? unref(options.search) : "").trim());

	const query = useQuery({
		queryKey: computed(() =>
			boardKeys.view(pipelineCode.value, term.value, options?.parent?.value),
		),
		queryFn: async (): Promise<BoardView> => {
			const query = new URLSearchParams();
			if (options?.parent?.value)
				for (const [key, value] of Object.entries(options.parent.value)) query.set(key, value);
			if (term.value) query.set("search", term.value);
			if (options?.perStage) query.set("perStage", String(options.perStage));
			const suffix = query.toString();
			return request(`/v3/board/pipelines/${pipelineCode.value}${suffix ? `?${suffix}` : ""}`);
		},
		enabled: computed(() => pipelineCode.value.length > 0),
		/**
		 * O quadro fica na tela enquanto o termo é digitado — sem isto cada tecla esvazia
		 * as colunas e a pessoa busca contra uma página em branco.
		 *
		 * Só DENTRO do mesmo funil, e é o ponto todo: trocar de funil também troca a
		 * chave, e aproveitar a resposta anterior desenharia os cards do Vendas sob o
		 * título do Pós-venda até a resposta chegar.
		 */
		placeholderData: (previous, previousQuery) =>
			(previousQuery?.queryKey as readonly unknown[] | undefined)?.[1] === pipelineCode.value &&
			JSON.stringify(previousQuery?.queryKey[4]) === JSON.stringify(options?.parent?.value)
				? previous
				: undefined,
	});

	/**
	 * O SERVIDOR NÃO TEM ESTE FUNIL — dito aqui, e não lido do erro pela página.
	 *
	 * `?funil=` chega de fora: um link colado de outra conta, o endereço que sobrou depois
	 * de trocar de workspace, um funil apagado desde que alguém guardou o link. O servidor
	 * responde 404 (`PIPELINE_NOT_FOUND`) e a tela precisa de distinguir ISSO de qualquer
	 * outra falha para poder dizer o que houve em vez de desenhar um quadro sem colunas.
	 *
	 * A taxonomia do transporte mora na camada `api` — uma página não fala HTTP
	 * (`http-only-in-api`), e uma página que lesse `error.kind` seria a terceira cópia do
	 * mesmo contrato. Devolve o CÓDIGO recusado, e não um booleano, porque é ele que a
	 * frase mostra a quem colou o link.
	 */
	const notFound = computed(() =>
		query.error.value instanceof HttpError && query.error.value.kind === "notFound"
			? pipelineCode.value
			: "",
	);

	return { ...query, notFound };
}

/**
 * The rest of one column.
 *
 * The page is written INTO the board's cache rather than kept beside it, so everything
 * downstream — the optimistic move, the totals, the drag arithmetic — keeps reading one
 * list. A refetch (a move, the funnel's stream) drops back to the first page, which is
 * the honest thing: what the server just said is the board, and a column silently
 * carrying rows from three fetches ago is the kind of stale nobody can see.
 */
export function useLoadMoreCards(
	code: Ref<string> | string,
	search?: Ref<string>,
	parent?: Ref<BoardParent | undefined>,
) {
	const client = useQueryClient();
	const term = () => (search ? unref(search) : "").trim();

	return useMutation({
		mutationFn: async (input: { stage: string; offset: number; limit: number }) => {
			const key = boardKeys.view(unref(code), term(), parent?.value);
			const query = new URLSearchParams({
				offset: String(input.offset),
				limit: String(input.limit),
			});
			// A busca viaja junto: pedir mais de uma coluna filtrada tem de continuar dentro
			// do filtro, senão "carregar mais" alarga em silêncio o que está na tela.
			if (parent?.value)
				for (const [key, value] of Object.entries(parent.value)) query.set(key, value);
			if (term()) query.set("search", term());
			const page = await request<{ cards: BoardCard[] }>(
				`/v3/board/pipelines/${unref(code)}/stages/${input.stage}/cards?${query.toString()}`,
			);
			return { ...page, key };
		},
		onSuccess: (page) => {
			client.setQueryData<BoardView>(page.key, (old) => {
				if (!old) return old;
				// A card that moved into the first page between the two requests would
				// otherwise arrive twice, and two cards with the same key is a Vue warning
				// and a card that cannot be dragged.
				const known = new Set(old.cards.map((c) => c.recordId));
				const fresh = page.cards.filter((c) => !known.has(c.recordId));
				return fresh.length > 0 ? { ...old, cards: [...old.cards, ...fresh] } : old;
			});
		},
	});
}

/**
 * Moving a card, and now to a PLACE inside the column.
 *
 * `position` has been accepted by the endpoint since the board existed and this hook
 * dropped it on the floor — so dropping a card between two others put it wherever the
 * server's default put it, and the person who aimed watched it land somewhere else.
 *
 * It is the FINAL index the card should hold, counted with the dragged card already
 * removed from its old place. The server opens the gap and renumbers both columns, so the
 * next drop's arithmetic starts from what the screen is showing. Omitted means "no
 * chosen place" — a plain move from one column to another.
 */
export function useMoveCard(code: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { recordId: string; stage: string; position?: number }) =>
			request(`/v3/board/pipelines/${unref(code)}/records/${input.recordId}/move`, {
				method: "POST",
				body: {
					stage: input.stage,
					...(input.position !== undefined ? { position: input.position } : {}),
				},
			}),

		/**
		 * O card anda no instante em que a mão solta.
		 *
		 * Antes disto a tela esperava DUAS idas ao servidor — o POST e o refetch do quadro
		 * inteiro que o `invalidate` disparava — e o card ficava parado no lugar antigo
		 * durante as duas. Numa mesa que arrasta dezenas de cards por dia, é a diferença
		 * entre uma ferramenta e um formulário.
		 *
		 * `cancelQueries` primeiro, e não é detalhe: um refetch já em voo chegaria DEPOIS
		 * desta escrita e devolveria o card para a coluna de origem — o card voltando
		 * sozinho meio segundo depois é pior do que a demora que isto veio corrigir.
		 */
		// Every reading of this board and not one key: the funnel is now cached once per
		// search term, and writing only into the unsearched one would leave the card
		// standing still on a filtered board — where somebody is just as likely to be.
		onMutate: async (input) => {
			const filter = { queryKey: [...boardKeys.board(unref(code)), "view"] };
			await client.cancelQueries(filter);

			const previous = client.getQueriesData<BoardView>(filter);
			client.setQueriesData<BoardView>(filter, (old) => (old ? withMove(old, input) : old));

			return { previous };
		},

		// O servidor recusou (etapa que sumiu, permissão, rede): o quadro volta a ser o
		// que era, inteiro. Sem isto a tela ficaria afirmando um movimento que não houve.
		onError: (_error, _input, context) => {
			for (const [key, data] of context?.previous ?? []) client.setQueryData(key, data);
		},

		// The board AND the entity list both change: moving a card mirrors the stage
		// onto the record's status field, so a stale table would show the old value.
		// Em `onSettled` e não em `onSuccess`: depois de uma falha a tela também precisa
		// voltar a concordar com o servidor.
		onSettled: () => {
			void client.invalidateQueries({ queryKey: ["board"] });
			void client.invalidateQueries({ queryKey: ["records"] });
		},
	});
}

/** The board as it will be once the server agrees. Pure, so every cached reading gets it. */
function withMove(
	board: BoardView,
	input: { recordId: string; stage: string; position?: number },
): BoardView {
	const target = board.stages.find((s) => s.code === input.stage);
	const moving = board.cards.find((c) => c.recordId === input.recordId);
	if (!target || !moving) return board;

	const rest = board.cards.filter((c) => c.recordId !== moving.recordId);
	const column = rest.filter((c) => c.stageId === target.id);
	const at = Math.min(Math.max(input.position ?? column.length, 0), column.length);

	// O relógio da etapa só reinicia quando a etapa muda de verdade. Reordenar
	// dentro da própria coluna não é uma entrada, e um "há 5 dias aqui" que vira
	// "agora" ao subir um card duas posições é a mesma mentira que o servidor
	// deixou de contar quando `reorderCard` nasceu.
	const entered = moving.stageId === target.id ? moving.enteredStageAt : new Date().toISOString();

	column.splice(at, 0, { ...moving, stageId: target.id, enteredStageAt: entered });

	const others = rest.filter((c) => c.stageId !== target.id);
	const renumbered = column.map((c, index) => ({ ...c, position: index }));
	// `stageTotals` fica como veio: são as contagens da coluna INTEIRA, e recalculá-las
	// aqui só acertaria a contagem — a soma continuaria a do servidor, porque a projeção
	// do card não traz necessariamente o campo de valor. Meia correção que dura até o
	// refetch é pior do que nenhuma.
	return { ...board, cards: [...others, ...renumbered] };
}

/**
 * Creating a funnel from the screen.
 *
 * Until now this was agent-only, which made the builder a bottleneck for
 * something a person can decide in ten seconds. The gate that matters is still
 * on the server: the entity must exist, and two stages minimum — one stage is a
 * list, not a funnel.
 */
export function useCreatePipeline() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			code: string;
			name: string;
			entityCode: string;

			stages: Array<{ code: string; name: string; color?: string }>;
		}) => request<PipelineSummary>("/v3/board/pipelines", { method: "POST", body: input }),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["board"] });
			void client.invalidateQueries({ queryKey: ["structure"] });
			void client.invalidateQueries({ queryKey: ["records"] });
		},
	});
}

/**
 * The goal of a step.
 *
 * What this deliberately cannot do is give a stage a prompt: it writes two
 * sentences the agent receives as context, and the agent's behaviour still comes
 * from its own base and persona. In V2 a stage could reprogram the agent and
 * nobody could say afterwards which instructions were in force.
 */
export function useUpdateStage(code: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async ({ stageCode, ...patch }: StagePatch & { stageCode: string }) =>
			request<Stage>(`/v3/board/pipelines/${unref(code)}/stages/${stageCode}`, {
				method: "PATCH",
				// Only what was passed. `undefined` leaves a field alone and `null`
				// clears it, and sending every key would turn "rename" into "wipe the
				// objective too".
				body: patch,
			}),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["board"] });
			void client.invalidateQueries({ queryKey: ["structure"] });
			void client.invalidateQueries({ queryKey: ["records"] });
		},
	});
}

/**
 * What a stage edit may carry.
 *
 * `undefined` means "leave it"; `null` means "clear it". Everything here has
 * existed on the table since the board was built and none of it was reachable.
 */
export interface StagePatch {
	name?: string;
	color?: string;
	objective?: string | null;
	exitWhen?: string | null;

	isTerminal?: boolean;
}

export function useAddStage(code: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { code: string; name: string; color?: string }) =>
			request<Stage>(`/v3/board/pipelines/${unref(code)}/stages`, {
				method: "POST",
				body: input,
			}),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["board"] });
			void client.invalidateQueries({ queryKey: ["structure"] });
			void client.invalidateQueries({ queryKey: ["records"] });
		},
	});
}

export function useRemoveStage(code: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (stageCode: string) =>
			request(`/v3/board/pipelines/${unref(code)}/stages/${stageCode}`, { method: "DELETE" }),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["board"] });
			void client.invalidateQueries({ queryKey: ["structure"] });
			void client.invalidateQueries({ queryKey: ["records"] });
		},
	});
}

export function useReorderStages(code: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (order: string[]) =>
			request(`/v3/board/pipelines/${unref(code)}/stages/order`, {
				method: "POST",
				body: { order },
			}),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["board"] });
			void client.invalidateQueries({ queryKey: ["structure"] });
			void client.invalidateQueries({ queryKey: ["records"] });
		},
	});
}

export function useUpdatePipeline(code: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: {
			name?: string;
			description?: string | null;
			cardFields?: readonly string[];
			/** Put it away, or bring it back — never a delete (migration 0047). */
			archived?: boolean;
			/** The funnel that opens first. Setting one clears whichever held it. */
			isDefault?: boolean;
		}) => request(`/v3/board/pipelines/${unref(code)}`, { method: "PATCH", body: input }),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["board"] });
			void client.invalidateQueries({ queryKey: ["structure"] });
			void client.invalidateQueries({ queryKey: ["records"] });
		},
	});
}

/**
 * APONTAR O FUNIL PARA OUTRA TELA — enquanto ele ainda está vazio.
 *
 * Rota própria (`PATCH .../entity`) e não mais um campo do PATCH do funil: é o
 * único edit com uma pré-condição, e a recusa nomeia QUAL — `PIPELINE_HAS_CARDS`
 * com a contagem, `ENTITY_TAKEN` com o funil que já ocupa a tela, `NO_ENTITY`. A
 * frase do servidor sobe como está: cada uma diz o que a pessoa tem de fazer, e
 * traduzi-las aqui seria uma segunda escrita da mesma regra.
 *
 * Invalida `structure` além de `board`: o funil passa a ler outra tabela, e a
 * lista de campos do card é da tela nova. A chave vai LITERAL porque uma feature
 * não importa outra — é o prefixo que `structureKeys.entities()` monta.
 */
export function useRebindPipeline(code: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { entityCode: string }) =>
			request(`/v3/board/pipelines/${unref(code)}/entity`, { method: "PATCH", body: input }),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["board"] });
			void client.invalidateQueries({ queryKey: ["structure"] });
		},
	});
}

/**
 * Puts an existing record on a board — in a chosen stage, or in the first one.
 *
 * `stageCode` is the same word the contact version already uses, and `PlaceOnBoard`
 * has understood it since Fase B: it picks the stage by code and refuses with
 * `unknown_pipeline` when the code is not there, so a typo never lands the card
 * quietly in the first column.
 *
 * A tela ainda não escreve "criado em Negociação": espera o quadro voltar e mostra onde
 * o card REALMENTE está. Afirmar antes seria afirmar sobre uma etapa que o servidor pode
 * ter recusado.
 */
/**
 * A MESMA escrita, com o funil no pedido em vez de no `setup`.
 *
 * `useUpdatePipeline(code)` serve a tela de UM funil, onde o código é fixo enquanto a
 * tela vive. A lista escreve em qualquer linha — arquivar a terceira, tornar padrão a
 * quinta — e um `ref` trocado antes de cada `mutateAsync` seria uma corrida perdida na
 * segunda pessoa que clica depressa: o `mutationFn` lê o `ref` num microtique posterior,
 * e o segundo clique já o teria mudado.
 *
 * Mesma rota, mesma invalidação, mesmo corpo. O que muda é de onde vem o código.
 */
export function useWriteFunnel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: {
			code: string;
			name?: string;
			description?: string | null;
			cardFields?: readonly string[];
			/** Guardar, ou trazer de volta — nunca apagar (migração 0047). */
			archived?: boolean;
			/** O funil que abre primeiro. Marcar um limpa o que estava. */
			isDefault?: boolean;
		}) => {
			const { code, ...patch } = input;
			return request(`/v3/board/pipelines/${code}`, { method: "PATCH", body: patch });
		},
		onSuccess: () => client.invalidateQueries({ queryKey: ["board"] }),
	});
}

/**
 * Gives a card an owner, or puts it back in the pool.
 *
 * `assigned_to` was written by putting a contact on a funnel and by the `assign_user`
 * automation effect, and by no person anywhere — so who is working a deal was a fact
 * only a rule could state. The card query is invalidated as well as the board, because
 * the panel that made the change is showing the old name until it is.
 */
export function useAssignCard() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { pipelineCode: string; recordId: string; assignedTo: string | null }) =>
			request(`/v3/board/pipelines/${input.pipelineCode}/records/${input.recordId}`, {
				method: "PATCH",
				body: { assignedTo: input.assignedTo },
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: ["board"] }),
	});
}

/**
 * Puts a contact on a funnel, creating the record that links them.
 *
 * The board list is invalidated as well as the pipelines: the new card has to appear
 * without a reload, and the person doing this is usually looking at the board next.
 */
export function usePutContactOnFunnel() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: {
			pipelineCode: string;
			contactId: string;
			title?: string;
			/** Which stage it lands on. Absent means the first, which is what it always did. */
			stageCode?: string;
			/**
			 * O que o funil exige e o nascimento não teria — `birthFields`, preenchido por
			 * quem está a pôr a pessoa lá.
			 *
			 * "Quem move fornece o que a entidade exige": gente fornece preenchendo, o
			 * agente fornece perguntando. Nunca se adivinha um valor — por isso não há
			 * default nenhum aqui, e omitir a chave é o mesmo que não ter respondido.
			 */
			values?: Record<string, unknown>;
		}) =>
			request<{ cardId: string; stageId: string; recordId: string }>(
				`/v3/board/pipelines/${input.pipelineCode}/contacts`,
				{
					method: "POST",
					body: {
						contactId: input.contactId,
						...(input.title ? { title: input.title } : {}),
						...(input.stageCode ? { stageCode: input.stageCode } : {}),
						...(input.values ? { values: input.values } : {}),
					},
				},
			),
		onSuccess: (_result, input) => {
			void client.invalidateQueries({ queryKey: boardKeys.board(input.pipelineCode) });
			void client.invalidateQueries({ queryKey: boardKeys.pipelines() });
		},
	});
}

/**
 * One card with everything a detail panel needs, in one request.
 *
 * Four requests would mean four loading states and a panel that assembles itself in
 * front of the person. `enabled` on the id so closing the panel stops the polling and
 * opening it does not race the click.
 */
export interface CardDetail {
	readonly card: {
		readonly id: string;
		readonly recordId: string;
		readonly assignedTo: string | null;
		readonly enteredStageAt: string;
		/** From the SERVER's clock — a browser minutes out would misreport days. */
		readonly secondsInStage: number;
	};
	readonly pipeline: {
		readonly code: string;
		readonly name: string;
		readonly entityCode: string;

		readonly stages: ReadonlyArray<{
			readonly code: string;
			readonly name: string;
			readonly color: string;
			readonly isTerminal: boolean;
			readonly objective: string | null;
		}>;
	};
	readonly stage: { readonly code: string; readonly name: string } | null;
	readonly record: Record<string, unknown> | null;
	readonly moves: ReadonlyArray<{
		readonly id: string;
		readonly from: { code: string; name: string } | null;
		readonly to: { code: string; name: string } | null;
		readonly movedByName: string | null;
		readonly secondsInPrevious: number;
		readonly at: string;
	}>;
}

export function useCardDetail(pipeline: Ref<string> | string, recordId: Ref<string> | string) {
	const id = computed(() => unref(recordId));
	return useQuery({
		queryKey: computed(() => ["board", "record", unref(pipeline), id.value] as const),
		enabled: computed(() => Boolean(id.value)),
		queryFn: () =>
			request<CardDetail>(`/v3/board/pipelines/${unref(pipeline)}/records/${id.value}`),
	});
}

/**
 * Per-stage numbers for a funnel: how many are here, and how long they take to leave.
 *
 * `staleTime` is generous — these are medians over the funnel's whole history, and they
 * do not move meaningfully between two glances at the same screen.
 */
export interface StageNumbers {
	readonly code: string;
	readonly name: string;
	readonly color: string;
	readonly isTerminal: boolean;
	readonly open: number;
	readonly medianWaiting: number;
	readonly longestWaiting: number;
	readonly left: number;
	readonly medianToLeave: number;
	/**
	 * Quanto ENTROU nesta etapa dentro da janela, somado no campo de valor do funil.
	 *
	 * A primeira frase que o dono de um funil procura — "fechei R$ 180.000 este mês" — e
	 * ela só é lida nas etapas terminais, onde chegar é o desfecho. Optional e `null`
	 * quando o funil não tem campo de valor, porque a alternativa seria um zero que se lê
	 * como "não fechei nada".
	 */
	readonly closedValue?: number | null;
}

/**
 * How many cards went from one stage to another, in the period asked for.
 *
 * A fact about a PAIR of stages, which is why it is not a field of `StageNumbers`:
 * conversion is drawn BETWEEN two columns, and hanging it off one of them forces a choice
 * of which — after which the last stage carries a number about a stage that is not there.
 */
export interface StageTransition {
	readonly fromStage: string;
	readonly toStage: string;
	readonly count: number;
}

/**
 * The window the numbers are read over. Days, or everything ever.
 *
 * Days and not two date pickers: "os últimos 30 dias" is the question people actually
 * ask of a funnel, and a pair of calendars to express it is four clicks for the common
 * case. The endpoint takes real dates, so a range picker can be added later without the
 * server learning about periods.
 */
export type FunnelPeriod = 7 | 30 | 90 | "all";

export function useFunnelNumbers(
	code: Ref<string> | string,
	period?: Ref<FunnelPeriod> | FunnelPeriod,
	/** Which field is the money. Chosen by the board, never a second time here. */
	valueField?: Ref<string | null> | string | null,
) {
	const pipeline = computed(() => unref(code));
	const window = computed<FunnelPeriod>(() => unref(period) ?? "all");
	const money = computed(() => unref(valueField) ?? "");

	return useQuery({
		// The period is PART of the key. Without it, switching from 90 days to 7 hands
		// back the cached answer for 90 — the numbers do not move, nothing errors, and
		// the person reads last quarter's conversion as last week's. Same for the field
		// being summed: two funnels' worth of money under one key.
		queryKey: computed(
			() => ["board", pipeline.value, "numbers", window.value, money.value] as const,
		),
		enabled: computed(() => Boolean(pipeline.value)),
		queryFn: () => {
			const query = new URLSearchParams();
			const since = sinceFor(window.value);
			// Only `from`. The other end is now, and sending it would freeze the window at
			// the moment the query was built.
			if (since) query.set("from", since);
			// Sem campo nomeado a resposta volta com `closedValue: null` em toda etapa, e o
			// painel não escreve dinheiro nenhum — que é o certo: um funil sem valor não
			// fechou zero, não tem número.
			if (money.value) query.set("valueField", money.value);
			const suffix = query.toString();
			return request<{ stages: StageNumbers[]; transitions: StageTransition[] }>(
				`/v3/board/pipelines/${pipeline.value}/numbers${suffix ? `?${suffix}` : ""}`,
			);
		},
		staleTime: 60_000,
	});
}

/**
 * Dinheiro, escrito de um jeito só.
 *
 * O cabeçalho da coluna e o painel de números somam o MESMO campo e apareciam lado a
 * lado; formatados cada um no seu arquivo, o mesmo valor sairia "R$ 42.300" num e
 * "42300" no outro. Sem centavos porque o número aqui é uma ordem de grandeza — quem
 * precisa do centavo abre o registro.
 */
export function formatAmount(total: number, kind: "currency" | "number"): string {
	// The PANEL's language and the WORKSPACE's currency, which are two different
	// questions: a colleague reading in English still sees the money the business
	// actually bills in. Hardcoding "pt-BR"/"BRL" answered both wrongly at once.
	const ctx = panelContext();
	return formatNumber(total, ctx, {
		style: kind === "currency" ? "currency" : "decimal",
		currency: ctx.currency,
		maximumFractionDigits: 0,
	});
}

/** The start of a period, as the server wants it. `all` has no start. */
function sinceFor(period: FunnelPeriod): string | null {
	if (period === "all") return null;
	return new Date(Date.now() - period * 24 * 60 * 60 * 1000).toISOString();
}
